import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import pb from '../../lib/pocketbase';
import './ClientProfileForm.css';

const ClientProfileForm = ({ user, customer, setCustomer, application = null }) => {
    const navigate = useNavigate();
    // State pour le formulaire
    const [profile, setProfile] = useState({
        name: '',
        company: '',
        email: '',
        phone: '',
        address: '',
        postalCode: '',
        city: ''
    });

    // UI States
    const [isEditing, setIsEditing] = useState(false); // Mode édition (Modal)
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    // Address Suggestions State
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [isSearchingAddress, setIsSearchingAddress] = useState(false);

    // Helper to parse existing address if zip/city are missing
    const getInitialProfile = (cust, usr) => {
        if (!cust) return {
            name: '', company: '', email: '', phone: '',
            address: '', postalCode: '', city: ''
        };

        let addr = cust.address || '';
        let zip = cust.postal_code || cust.postalCode || '';
        let cityStr = cust.city || '';

        // Auto-detect Zip/City from address line if missing
        // Regex robust: matches any content (including newlines) before 5 digits, then 5 digits, then rest
        if (!zip && !cityStr && addr) {
            const match = addr.match(/^([\s\S]*?)(\d{5})\s*([\s\S]*)$/);
            if (match) {
                addr = match[1].trim();
                zip = match[2];
                cityStr = match[3].trim();
            }
        }

        return {
            name: cust.name || cust.full_name || '',
            company: cust.company || '',
            email: cust.email || usr?.email || '',
            phone: cust.phone || '',
            address: addr,
            postalCode: zip,
            city: cityStr
        };
    };

    // Sync profile with customer data
    useEffect(() => {
        if (customer) {
            setProfile(getInitialProfile(customer, user));
        }
    }, [customer, user]);

    // 3D Card Logic
    const [cardRotation, setCardRotation] = useState({ x: 0, y: 0 });
    const [isCardDragging, setIsCardDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const handleMove = (e) => {
            if (!isCardDragging) return;
            const deltaX = e.clientX - dragStart.x;
            const deltaY = e.clientY - dragStart.y;
            setCardRotation(prev => ({
                x: Math.max(-60, Math.min(60, prev.x - deltaY * 0.5)),
                y: prev.y + deltaX * 0.5
            }));
            setDragStart({ x: e.clientX, y: e.clientY });
        };
        const handleUp = () => setIsCardDragging(false);
        if (isCardDragging) {
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', handleUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };
    }, [isCardDragging, dragStart]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfile(prev => ({ ...prev, [name]: value }));

        // Trigger address search if name is address
        if (name === 'address' && value.length > 3) {
            handleAddressSearch(value);
        } else if (name === 'address') {
            setAddressSuggestions([]);
        }
    };

    const handleAddressSearch = async (query) => {
        setIsSearchingAddress(true);
        try {
            const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`);
            const data = await res.json();
            setAddressSuggestions(data.features || []);
        } catch (e) {
            console.error("Address search error", e);
        } finally {
            setIsSearchingAddress(false);
        }
    };

    const selectAddress = (feature) => {
        const { name, postcode, city } = feature.properties;
        setProfile(prev => ({
            ...prev,
            address: name,
            postalCode: postcode,
            city: city
        }));
        setAddressSuggestions([]);
    };

    const handleReset = () => {
        if (!customer) return;
        setMessage('');
        setProfile(getInitialProfile(customer, user));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!customer) return;
        setSaving(true);
        setMessage('');

        try {
            const payload = {
                name: profile.name,
                company: profile.company,
                email: profile.email,
                phone: profile.phone,
                address: profile.address,
                postal_code: profile.postalCode,
                city: profile.city,
            };

            await pb.collection('customers').update(customer.id, payload);
            setMessage('Vos informations ont été mises à jour avec succès.');

            // Rafraîchir
            try {
                const fresh = await pb.collection('customers').getOne(customer.id);
                if (setCustomer) setCustomer(fresh);
            } catch { }

            // Fermer le modal après succès (avec un petit délai pour voir le message ?)
            // On peut laisser le modal ouvert un instant ou le fermer.
            // Pour l'expérience, mieux vaut montrer le succès puis l'utilisateur ferme ou ça se ferme.
            // On va juste laisser l'utilisateur fermer pour voir le message.

        } catch (error) {
            console.error(error);
            alert("Une erreur est survenue lors de la mise à jour.");
        } finally {
            setSaving(false);
        }
    };



    // Quick Update Handler for Inline Editing
    const handleQuickUpdate = async (field, value) => {
        if (!customer) return;
        try {
            // Optimistic update
            setProfile(prev => ({ ...prev, [field]: value }));

            // API Update
            const payload = { [field === 'postalCode' ? 'postal_code' : field]: value };
            await pb.collection('customers').update(customer.id, payload);

            // Background refresh to ensure consistency
            const fresh = await pb.collection('customers').getOne(customer.id);
            if (setCustomer) setCustomer(fresh);

        } catch (error) {
            console.error("Quick update error:", error);
            // Revert on error (optional, or just alert)
            alert("Erreur lors de la sauvegarde.");
        }
    };

    // Inline Editable Field Component
    const EditableField = ({ label, name, value, iconColor, iconBg, svgIcon, placeholder }) => {
        const [editing, setEditing] = useState(false);
        const [tempVal, setTempVal] = useState(value);
        const [busy, setBusy] = useState(false);

        useEffect(() => setTempVal(value), [value]);

        const save = async () => {
            setBusy(true);
            await handleQuickUpdate(name, tempVal);
            setBusy(false);
            setEditing(false);
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') { setTempVal(value); setEditing(false); }
        };

        if (editing) {
            return (
                <div className="premium-info-card editing" style={{ padding: 12 }}>
                    <div className="info-icon-box" style={{ width: 40, height: 40, color: iconColor, background: iconBg }}>
                        {svgIcon}
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div className="info-label">{label}</div>
                        <input
                            autoFocus
                            className="inline-input"
                            value={tempVal}
                            onChange={e => setTempVal(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            disabled={busy}
                        />
                    </div>
                    <div className="inline-actions">
                        <button className="inline-btn save" onClick={save} disabled={busy}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        </button>
                        <button className="inline-btn cancel" onClick={() => { setTempVal(value); setEditing(false); }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="premium-info-card group-hover-trigger">
                <div className="info-icon-box" style={{ color: iconColor, background: iconBg }}>
                    {svgIcon}
                </div>
                <div style={{ flex: 1 }}>
                    <div className="info-label">{label}</div>
                    <div className="info-value" style={{ wordBreak: 'break-all' }}>{value || '—'}</div>
                </div>
                <button className="edit-pen-btn" onClick={() => setEditing(true)} aria-label="Modifier">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
                </button>
            </div>
        );
    };

    // Rendu en mode "Lecture seule" (Premium Style + Inline Edit)
    const renderReadView = () => (
        <div className="profile-read-view">
            {/* Recruitment Status */}
            {application && (
                <div className="card" style={{ border: '1px solid #10b98133', borderRadius: 16, padding: 24, background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(255,255,255,0.05) 100%)', color: 'var(--text-primary)', marginBottom: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                            Candidature Partenaire
                        </h3>
                        <span style={{
                            fontSize: '0.75rem', fontWeight: 700,
                            background: application.status === 'pending' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                            color: application.status === 'pending' ? '#f59e0b' : '#10b981',
                            padding: '4px 12px', borderRadius: 20, textTransform: 'uppercase'
                        }}>
                            {application.status === 'pending' ? 'En cours' : 'Validée'}
                        </span>
                    </div>
                    <p style={{ margin: '0', color: 'var(--muted)', fontSize: '0.95rem' }}>
                        Votre demande pour rejoindre l'équipe est bien enregistrée.
                        Vous recevrez une notification dès qu'elle sera traitée !
                    </p>
                </div>
            )}

            <div style={{ marginBottom: 32 }}>
                <h3 className="profile-section-title" style={{ fontWeight: 800, fontSize: '1.2rem', marginBottom: 16, color: 'var(--text-primary)' }}>Coordonnées</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>

                    <EditableField
                        label="Nom complet"
                        name="name"
                        value={profile.name}
                        iconColor="#3b82f6"
                        iconBg="rgba(59, 130, 246, 0.1)"
                        placeholder="Votre nom"
                        svgIcon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>}
                    />

                    <EditableField
                        label="Entreprise"
                        name="company"
                        value={profile.company}
                        iconColor="#8b5cf6"
                        iconBg="rgba(139, 92, 246, 0.1)"
                        placeholder="Nom de l'entreprise"
                        svgIcon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="21" width="18" height="2" transform="translate(0 -2)"></rect><path d="M5 21V7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v14"></path><path d="M9 10a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10H9V10z"></path></svg>}
                    />

                    <EditableField
                        label="Email"
                        name="email"
                        value={profile.email}
                        iconColor="#10b981"
                        iconBg="rgba(16, 185, 129, 0.1)"
                        placeholder="email@exemple.com"
                        svgIcon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>}
                    />

                    <EditableField
                        label="Téléphone"
                        name="phone"
                        value={profile.phone}
                        iconColor="#f59e0b"
                        iconBg="rgba(245, 158, 11, 0.1)"
                        placeholder="06 00 00 00 00"
                        svgIcon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>}
                    />
                </div>
            </div>

            <div style={{ marginBottom: 32 }}>
                <h3 className="profile-section-title" style={{ fontWeight: 800, fontSize: '1.2rem', marginBottom: 16, color: 'var(--text-primary)' }}>Adresse de facturation</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>

                    <div style={{ gridColumn: 'span 2' }}>
                        <EditableField
                            label="Adresse"
                            name="address"
                            value={profile.address}
                            iconColor="#ec4899"
                            iconBg="rgba(236, 72, 153, 0.1)"
                            placeholder="Numéro et rue"
                            svgIcon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>}
                        />
                    </div>

                    <EditableField
                        label="Code Postal"
                        name="postalCode"
                        value={profile.postalCode || ''}
                        iconColor="#6366f1"
                        iconBg="rgba(99, 102, 241, 0.1)"
                        placeholder="75000"
                        svgIcon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18M5 21V7l8-4 8 4v14M8 21v-4h8v4"></path></svg>}
                    />

                    <EditableField
                        label="Ville"
                        name="city"
                        value={profile.city || ''}
                        iconColor="#6366f1"
                        iconBg="rgba(99, 102, 241, 0.1)"
                        placeholder="Ville"
                        svgIcon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18M5 21V7l8-4 8 4v14M8 21v-4h8v4"></path></svg>}
                    />
                </div>
            </div>

            {/* Wallet Section (VIP Card) */}
            <div className="wallet-section" style={{ marginBottom: 40, marginTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <h3 className="profile-section-title" style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: 'var(--text-primary)' }}>Ma Carte Membre</h3>
                    <button className="btn-text" style={{ fontSize: '0.85rem', color: '#3b82f6', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                        Voir les avantages →
                    </button>
                </div>

                <div className="vip-card-scene" style={{ perspective: '1200px', maxWidth: 400, margin: '0 auto', height: 240, position: 'relative' }}>
                    <div
                        className="vip-card-3d"
                        onMouseDown={(e) => {
                            setIsCardDragging(true);
                            setDragStart({ x: e.clientX, y: e.clientY });
                        }}
                        style={{
                            width: '100%', height: '100%', position: 'relative',
                            transformStyle: 'preserve-3d',
                            transform: `rotateX(${cardRotation.x}deg) rotateY(${cardRotation.y}deg)`,
                            transition: isCardDragging ? 'none' : 'transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                            cursor: isCardDragging ? 'grabbing' : 'grab'
                        }}
                    >
                        {/* RECTO */}
                        <div className="vip-card-face vip-card-front" style={{
                            position: 'absolute', inset: 0, backfaceVisibility: 'hidden',
                            background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
                            borderRadius: 16, padding: 24, overflow: 'hidden',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.4)',
                            display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
                        }}>
                            <div style={{ position: 'absolute', top: -100, right: -100, width: 300, height: 300, background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)', filter: 'blur(40px)', pointerEvents: 'none' }}></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1, pointerEvents: 'none' }}>
                                <div>
                                    <div style={{ fontSize: '1rem', fontWeight: 700, fontFamily: 'sans-serif', letterSpacing: '0.05em', color: 'white' }}>InfoColab</div>
                                    <div style={{ fontSize: '0.6rem', fontWeight: 600, fontFamily: 'sans-serif', letterSpacing: '0.2em', opacity: 0.7, color: 'white' }}>AMBASSADOR</div>
                                </div>
                                <svg width="40" height="30" viewBox="0 0 50 35" fill="none" style={{ opacity: 0.9 }}>
                                    <rect width="50" height="35" rx="4" fill="#fbbf24" fillOpacity="0.2" stroke="#fbbf24" strokeWidth="0.5" />
                                    <path d="M10 0V35" stroke="#fbbf24" strokeOpacity="0.5" />
                                    <path d="M40 0V35" stroke="#fbbf24" strokeOpacity="0.5" />
                                </svg>
                            </div>
                            <div style={{ position: 'relative', zIndex: 1, margin: '20px 0', pointerEvents: 'none' }}>
                                <div style={{ fontSize: '0.7rem', opacity: 0.6, marginBottom: 4, fontFamily: 'sans-serif', color: 'white' }}>CODE PARRAINAGE</div>
                                <div style={{ fontSize: '1.2rem', fontWeight: 600, letterSpacing: '0.1em', textShadow: '0 2px 4px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', gap: 10, fontFamily: "'Courier New', monospace", color: 'white' }}>
                                    {user?.id?.substring(0, 4) || 'XXXX'} •••• •••• {(user?.id?.substring(user.id.length - 4)) || 'XXXX'}
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', position: 'relative', zIndex: 1, pointerEvents: 'none' }}>
                                <div>
                                    <div style={{ fontSize: '0.6rem', opacity: 0.6, marginBottom: 2, fontFamily: 'sans-serif', color: 'white' }}>TITULAIRE</div>
                                    <div style={{ fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'white' }}>{profile.name || 'MEMBRE'}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '0.6rem', opacity: 0.6, marginBottom: 2, fontFamily: 'sans-serif', color: 'white' }}>MEMBRE DEPUIS</div>
                                    <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'white' }}>2024</div>
                                </div>
                            </div>
                            <div className="card-shine-effect" style={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                background: 'linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.05) 25%, transparent 30%)',
                                transform: 'translateX(-100%)', animation: 'shine 6s infinite linear', pointerEvents: 'none'
                            }}></div>
                        </div>

                        {/* VERSO */}
                        <div className="vip-card-face vip-card-back" style={{
                            position: 'absolute', inset: 0, backfaceVisibility: 'hidden',
                            transform: 'rotateY(180deg)',
                            background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
                            borderRadius: 16, overflow: 'hidden',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.4)',
                            display: 'flex', flexDirection: 'column'
                        }}>
                            <div style={{ height: 40, background: '#000', marginTop: 20, width: '100%' }}></div>
                            <div style={{ padding: '10px 24px', display: 'flex', gap: 20, alignItems: 'center', flex: 1 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ height: 30, background: '#fff', opacity: 0.8, marginBottom: 8, display: 'flex', alignItems: 'center', paddingLeft: 10, fontFamily: 'cursive', color: '#000', fontSize: '1rem' }}>
                                        {profile.name}
                                    </div>
                                    <div style={{ fontSize: '0.6rem', color: '#94a3b8', lineHeight: 1.2 }}>
                                        Carte de membre InfoColab.
                                    </div>
                                </div>
                                <div style={{ width: 80, height: 80, background: 'white', padding: 4, borderRadius: 4 }}>
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`${window.location.origin}/onboarding?ref=${user?.id?.substring(0, 8)}`)}`}
                                        alt="QR" style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="wallet-actions" style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24 }}>
                    <button className="wallet-btn" style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '10px 20px', borderRadius: 12, border: 'none',
                        background: 'var(--accent-color, #3b82f6)', color: 'white', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                        boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)'
                    }} onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/onboarding?ref=${user?.id?.substring(0, 8)}`);
                        alert('Code copié !'); // Simple alert or toast if available
                    }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                        Copier mon code
                    </button>
                </div>
            </div>



            <style>{`
                .premium-info-card {
                    display: flex; align-items: center; gap: 16px;
                    padding: 20px;
                    background: var(--card-bg, #ffffff);
                    border: 1px solid var(--border-color, #e2e8f0);
                    border-radius: 16px;
                    transition: all 0.2s ease;
                }
                .premium-info-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                    border-color: var(--accent-color, #3b82f6);
                }
                .info-icon-box {
                    width: 48px; height: 48px;
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }
                .info-label {
                    font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
                    color: var(--muted, #64748b); margin-bottom: 4px;
                }
                .info-value {
                    font-size: 1rem; font-weight: 600; color: var(--text-primary, #1e293b);
                }
                /* Dark Mode Support via global variables or explicit overrides */
                .dark .premium-info-card {
                    background: #1e293b; /* Fallback if var not set */
                    border-color: rgba(255,255,255,0.1);
                }
            `}</style>

            <div className="profile-edit-trigger">
                <button className="profile-btn btn-primary" onClick={() => { setMessage(''); setIsEditing(true); }}>
                    Modifier mes informations
                </button>
            </div>


            {/* Recruitment CTA - BOTTOM if NOT sent */}
            {
                !application && (
                    <div style={{ marginTop: 30 }}>
                        <div className="card" style={{ border: '1px solid #3b82f6', borderRadius: 12, padding: 24, background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', color: 'white' }}>
                            <h3 style={{ marginTop: 0, color: 'white', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                                Rejoindre l'équipe
                            </h3>
                            <p style={{ color: 'rgba(255,255,255,0.9)', maxWidth: 600, fontSize: '0.95rem' }}>
                                Vous souhaitez aller plus loin et devenir partenaire ? Postulez en quelques clics.
                            </p>
                            <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                                <button className="profile-btn" style={{ border: 'none', background: 'white', color: '#1e40af', fontWeight: 600, padding: '10px 14px', borderRadius: 10, cursor: 'pointer' }} onClick={() => navigate('/candidature', {
                                    state: {
                                        role: 'commercial',
                                        skipIntro: true,
                                        staff: {
                                            firstName: profile.name?.split(' ')[0] || '',
                                            lastName: profile.name?.split(' ').slice(1).join(' ') || '',
                                            email: profile.email || '',
                                            phone: profile.phone || ''
                                        }
                                    }
                                })}>
                                    Devenir Commercial
                                </button>
                                <button className="profile-btn" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', padding: '10px 14px', borderRadius: 10, cursor: 'pointer' }} onClick={() => navigate('/candidature', {
                                    state: {
                                        role: 'developpeur',
                                        skipIntro: true,
                                        staff: {
                                            firstName: profile.name?.split(' ')[0] || '',
                                            lastName: profile.name?.split(' ').slice(1).join(' ') || '',
                                            email: profile.email || '',
                                            phone: profile.phone || ''
                                        }
                                    }
                                })}>
                                    Devenir Développeur
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

        </div >
    );

    // Rendu du formulaire (Modal Content)
    const renderForm = () => (
        <form className="client-profile-form" onSubmit={handleSubmit}>
            {message && <div className="profile-message success">{message}</div>}

            {/* Ligne 1 */}
            <div className="form-row">
                <div className="form-group">
                    <label className="form-label">Nom complet</label>
                    <input
                        type="text"
                        name="name"
                        className="form-input"
                        value={profile.name}
                        onChange={handleChange}
                        placeholder="Votre nom"
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">Entreprise</label>
                    <input
                        type="text"
                        name="company"
                        className="form-input"
                        value={profile.company}
                        onChange={handleChange}
                        placeholder="Nom de l'entreprise"
                    />
                </div>
            </div>

            {/* Ligne 2 */}
            <div className="form-row">
                <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                        type="email"
                        name="email"
                        className="form-input"
                        value={profile.email}
                        onChange={handleChange}
                        placeholder="adresse@email.com"
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">Téléphone</label>
                    <input
                        type="tel"
                        name="phone"
                        className="form-input"
                        value={profile.phone}
                        onChange={handleChange}
                        placeholder="06 12 34 56 78"
                    />
                </div>
            </div>

            {/* Adresse complète sur une ligne (3 colonnes) */}
            <div className="form-row" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
                <div className="form-group" style={{ position: 'relative' }}>
                    <label className="form-label">Adresse</label>
                    <input
                        type="text"
                        name="address"
                        className="form-input"
                        value={profile.address}
                        onChange={handleChange}
                        placeholder="Numéro et rue"
                        autoComplete="off"
                    />
                    {isSearchingAddress && (
                        <div style={{ position: 'absolute', right: 10, top: 38 }}>
                            <div className="spinner-mini" style={{ width: 14, height: 14, border: '2px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }}></div>
                        </div>
                    )}
                    {addressSuggestions.length > 0 && (
                        <div className="address-suggestions" style={{
                            position: 'absolute', top: '100%', left: 0, right: 0,
                            zIndex: 100, background: 'white', border: '1px solid #e5e7eb',
                            borderRadius: 8, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                            marginTop: 4, overflow: 'hidden'
                        }}>
                            {addressSuggestions.map((feat, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => selectAddress(feat)}
                                    style={{
                                        padding: '10px 12px', cursor: 'pointer', borderBottom: idx < addressSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                                        fontSize: '0.85rem'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                >
                                    <strong>{feat.properties.name}</strong>, {feat.properties.postcode} {feat.properties.city}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="form-group">
                    <label className="form-label">Code postal</label>
                    <input
                        type="text"
                        name="postalCode"
                        className="form-input"
                        value={profile.postalCode}
                        onChange={handleChange}
                        placeholder="75000"
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">Ville</label>
                    <input
                        type="text"
                        name="city"
                        className="form-input"
                        value={profile.city}
                        onChange={handleChange}
                        placeholder="Votre ville"
                    />
                </div>
            </div>

            <div className="form-actions">
                <button type="button" className="profile-btn btn-secondary" onClick={handleReset}>
                    Réinitialiser
                </button>
                <button type="submit" className="profile-btn btn-primary" disabled={saving || !customer}>
                    {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
            </div>
        </form>
    );

    return (
        <div className="client-profile-container">
            <header className="client-profile-header">
                <h2>Vos informations</h2>
                <p>Consultez vos coordonnées et informations de facturation</p>
            </header>

            {/* Vue Lecture Seule */}
            {renderReadView()}

            {/* Modal d'édition */}
            {isEditing && (
                <div className="profile-modal-overlay" onClick={() => setIsEditing(false)}>
                    <div className="profile-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="profile-modal-header">
                            <h2>Modifier mes informations</h2>
                            <button className="modal-close-btn" onClick={() => setIsEditing(false)}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="profile-modal-body">
                            {renderForm()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientProfileForm;
